import {
  Radio as StoreblocksRadio,
  Props as StoreblocksRadioProps,
} from "@storeblocks/radio";
import { BooleanOption, BooleanRadioOptions } from "./radioTypes";
import {
  mapBooleanOptionToStringOptiton,
  mapStringOptionToBooleanOption,
} from "./radioUtils";

import { WrapLgMarginBottom } from "App/global-style";
import { useTexts } from "Components/TextContext";
import { OptionLabel } from "Components/TextContext/commonComponentEnums";
import { useButtonTexts } from "Store/texts/hooks";
import { radioTrackId } from "Utils/analytics";
import { nullableBooleanToNullableFormBoolean } from "Utils/formUtils";
import { useField } from "formik";
import React from "react";

type Props = Omit<StoreblocksRadioProps, "onChange" | "value" | "options"> &
  BooleanRadioOptions & {
    name: string;
    isLoading?: boolean;
    yesLabel?: string;
    noLabel?: string;
  };

export const RadioButtonBoolean = ({
  error,
  hint,
  id,
  label,
  name,
  direction = "horizontal",
  onChange,
  yesLabel,
  noLabel,
}: Props): React.ReactElement => {
  const { getText } = useTexts();
  const [field, { touched }, helpers] = useField(name);
  const [{ readMore, readLess }, lang] = useButtonTexts();

  const yesNoOptions: BooleanOption[] = [
    {
      id: `${name}-yes`,
      value: true,
      label: yesLabel ?? getText(OptionLabel.BOOLEAN_YES),
    },
    {
      id: `${name}-no`,
      value: false,
      label: noLabel ?? getText(OptionLabel.BOOLEAN_NO),
    },
  ];

  return (
    <WrapLgMarginBottom>
      <StoreblocksRadio
        {...field}
        id={id}
        value={nullableBooleanToNullableFormBoolean(field.value)}
        onChange={async (event, option) => {
          const booleanOption = mapStringOptionToBooleanOption(option);
          await helpers.setValue(booleanOption.value);
          await helpers.setTouched(true);
          onChange?.(booleanOption);
        }}
        label={label}
        options={yesNoOptions
          .map(mapBooleanOptionToStringOptiton)
          .map((option) => ({
            ...option,
            labelProps: { tabIndex: -1 },
            inputProps: { onBlur: field.onBlur },
            trackid: radioTrackId(id),
          }))}
        direction={direction}
        error={error && touched ? error : undefined}
        hint={hint}
        hintProps={{
          maxLines: 6,
          readLessLabel: `${readLess[lang]}`,
          readMoreLabel: `${readMore[lang]}`,
        }}
      />
    </WrapLgMarginBottom>
  );
};
